export type RawChart = {
  name: string;
  icon_file: string;
  description: string;
  best_use?: string;
};

export type ChartItem = {
  id: string; // canonical chart_id when possible
  name: string;
  icon: string; // url to svg
  description: string;
  category: string;
  bestUse?: string;
};

// Map chart names to categories by heuristics
function categoryFor(name: string): string {
  const n = name.toLowerCase();
  if (/(sankey|chord|flow)/.test(n)) return 'Flow';
  if (/(pie|doughnut|treemap|waterfall|spine|polar\s*area)/.test(n)) return 'Part to whole';
  if (/(histogram|boxplot|violin|dot-plot|density|geo[-_\s]*map)/.test(n)) return 'Distribution';
  if (/(scatter|bubble|heatmap|radar|cluster|dendrogram|logistic\s*regression)/.test(n)) return 'Relationship';
  if (/(bump|slope|timeline|line|area|column[-_ ]?timeline|line[-_ ]?column|column[-_ ]?line|stepped\s*line|ribbon\s*chart|candlestick)/.test(n)) return 'Trend';
  if (/(bar[-_ ]?ordered|column[-_ ]?ordered|lollipop|rank)/.test(n)) return 'Rank';
  if (/(stacked|composition)/.test(n)) return 'Composition';
  if (/(bar|column|bullet)/.test(n)) return 'Compare';
  return 'Compare';
}

import { resolveIconSrc } from './iconMap';

let chartsCache: ChartItem[] | null = null;

// Export function to clear cache (useful for development/testing)
export function clearChartsCache() {
  chartsCache = null;
}

function normKey(s: string): string {
  return String(s || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

const renameMap: Record<string, string> = {
  'area': 'Area Stacked',
  'bar diverging': 'Diverging Bar – Single Value per Row',
  'bar diverging stacked': 'Diverging Stacked Bar – Segment Counts',
  'bar stacked': 'Bar Horizontal Stacked',
  'boxplot': 'Box Plot',
  'bubble': 'Scatter Bubble Size',
  'bullet': 'Bullet Graph',
  'bump': 'Line Ranked Bump',
  'candlestick': 'Candlestick OHLC',
  'chord': 'Filled-Chord Diagrams',
  'circle timeline': 'Proportional Circle timeline',
  'cluster': 'Cluster Plot With Ellipse',
  'column grouped': 'Bar Vertical Grouped',
  'column magnitude': 'Bar Vertical',
  'column ordered': 'Bar Vertical Sorted',
  'column stacked': 'Bar Vertical Stacked',
  'dendrogram': 'Dendrogram Hierarchical',
  'density': 'Density Curve KDE',
  'dot plot': 'Dumbbell Plot',
  'doughnut': 'Doughnut Hole',
  'funnel': 'Funnel Chart',
  'heatmap': 'Heatmap Matrix',
  'histogram': 'Histogram',
  'line': 'Line Chart',
  'line column': 'Line and Bar Combo',
  'line and bar combo': 'Line and Bar Combo',
  'lollipop h': 'Lollipop Horizontal',
  'lollipop v': 'Lollipop Vertical',
  'pie': 'Pie Chart',
  'polar area': 'Polar Area Barpolar',
  'population pyramids': 'Population Pyramid – Mirrored Bars',
  'population pyramis': 'Population Pyramid – Mirrored Bars',
  'priestley timeline': 'Gantt chart',
  'sankey': 'Sankey Diagram',
  'scatterplot': 'Scatter Plot',
  'scatterplot connected': 'Connected Scatter Lines',
  'slope': 'Slope Chart Two Points',
  'spine': 'Diverging 100% Stacked Bar – Likert %',
  'radar': 'Radar Scatterpolar Filled',
  'stepped line': 'Line Stepped',
  'treemap': 'Treemap Chart',
  'violin': 'Violin Plot',
  'waterfall': 'Waterfall Chart',
  'logistic regression': 'Logistic Regression',
};

import { getApiBaseUrl } from "@/lib/urls";

export async function loadCharts(): Promise<ChartItem[]> {
  // In development, always reload to get fresh icon URLs with cache-busting
  // This ensures updated icons are picked up immediately
  const isProduction = import.meta.env.PROD || import.meta.env.VERCEL === '1';
  if (chartsCache && isProduction) return chartsCache;
  // Clear cache in development to force fresh icon URL generation
  if (!isProduction) chartsCache = null;
  // In production (Vercel), use relative paths to serve from public folder
  // In development, use API URLs (Vite dev proxies /chart_icons → API)
  const API_BASE = ((): string => {
    if (isProduction) return ''; // Use relative paths in production (served from Vercel CDN)
    try {
      return getApiBaseUrl().replace(/\/$/, '');
    } catch { return ''; }
  })();
  // Add cache-busting in development to ensure fresh data
  const cacheBuster = isProduction ? '' : `?v=${Date.now()}`;
  const fallback = API_BASE ? `${API_BASE}/chart_icons/charts.json${cacheBuster}` : `/chart_icons/charts.json${cacheBuster}`;
  const fallbackUpdate = API_BASE ? `${API_BASE}/chart_icons/charts update.json${cacheBuster}` : `/chart_icons/charts update.json${cacheBuster}`;
  const fallbackCards = API_BASE ? `${API_BASE}/chart_icons/chartcard.json${cacheBuster}` : `/chart_icons/chartcard.json${cacheBuster}`;
  let data: { charts: RawChart[] } | null = null;
  let update: { charts?: RawChart[]; categories?: Record<string, string[]> } | null = null;
  let cards: any[] | null = null;
  try {
    const r = await fetch(fallback, { cache: 'no-store' });
    if (r.ok) data = await r.json();
  } catch {}
  try {
    const ru = await fetch(fallbackUpdate, { cache: 'no-store' });
    if (ru.ok) update = await ru.json();
  } catch {}
  try {
    const rc = await fetch(fallbackCards, { cache: 'no-store' });
    if (rc.ok) cards = await rc.json();
  } catch {}
  const raws = Array.isArray(data?.charts) ? data!.charts : [];
  const updatedCharts = Array.isArray(update?.charts) ? (update!.charts as RawChart[]) : [];
  const knownIconFiles = new Set<string>(raws.map((c) => c.icon_file));

  // Merge base and update lists keyed by icon filename to preserve icon mapping
  type PartialItem = { id: string; name: string; icon_file: string; description: string; category: string; best_use?: string };
  const merged = new Map<string, PartialItem>();
  const byIconUpdate = new Map<string, RawChart>();
  const byNameUpdate = new Map<string, RawChart>();
  for (const u of updatedCharts) {
    if (u?.icon_file) byIconUpdate.set(u.icon_file, u);
    if (u?.name) byNameUpdate.set(normKey(u.name), u);
  }
  const union = [...raws, ...updatedCharts.filter(u => !raws.some(b => b.icon_file === u.icon_file))];
  for (const c of union) {
    const nameKeyFromBase = normKey(c.name);
    const override = byIconUpdate.get(c.icon_file) || byNameUpdate.get(nameKeyFromBase) || null;
    const source = override || c;
    const isSlopeVariant = /^(slope\-(ranking|timeline))$/i.test(source.name);
    const resolvedName = isSlopeVariant ? 'slope' : source.name;
    const namePretty = resolvedName.replace(/[-_]+/g, ' ');
    const chosenIcon = isSlopeVariant ? 'slope-ranking.svg' : c.icon_file;
    // Descriptions and best_use must only come from the update file
    const resolvedKey = normKey(resolvedName);
    const aliasKey = resolvedKey === 'xy heatmap' ? 'heatmap' : resolvedKey;
    const updateEntry = byIconUpdate.get(c.icon_file) || byNameUpdate.get(resolvedKey) || byNameUpdate.get(aliasKey) || null;
    const desc = isSlopeVariant
      ? 'Lines connect values at two points to show change.'
      : (updateEntry?.description || '');
    const bestUse = updateEntry?.best_use;
    const catViaMap = (() => {
      const cats = update?.categories || {};
      for (const [cat, arr] of Object.entries(cats)) {
        if (arr.some(n => n.toLowerCase() === namePretty.toLowerCase())) return cat;
        if (arr.some(n => normKey(n) === aliasKey)) return cat;
      }
      return '';
    })();
    const category = catViaMap || categoryFor(namePretty);
    merged.set(chosenIcon, { id: resolvedName, name: namePretty, icon_file: chosenIcon, description: desc, category, best_use: bestUse });
  }

  // Exclude specific charts per UX request: remove any "column line*" and "column timeline"
  const itemsRaw: PartialItem[] = Array.from(merged.values())
    .filter((m) => {
      const id = (m.id || '').toLowerCase();
      if (id === 'column-timeline') return false;
      if (id.startsWith('column-line')) return false;
      // Remove heatmap, but keep XY heatmap (renamed below)
      if (id === 'heatmap') return false;
      // Drop any entries whose icon is not part of the shipped icon set
      if (!knownIconFiles.has(m.icon_file)) return false;
      return true;
    });

  // Deduplicate by normalized name; prefer entries with known icons (already ensured) and first occurrence wins
  const byName = new Map<string, PartialItem>();
  for (const m of itemsRaw) {
    const normalized = /xy[-_ ]?heatmap/i.test(m.name) ? 'heatmap' : m.name.toLowerCase();
    if (!byName.has(normalized)) byName.set(normalized, m);
  }

  const cardIds = new Set<string>(Array.isArray(cards) ? cards.map((c: any) => String(c?.chart_id || '')) : []);
  
  // Mapping from UI chart names (from charts.json) to chartcard.json IDs
  const uiNameToChartcardId: Record<string, string> = {
    'area': 'area_stacked',
    'bar-diverging': 'diverging_bar_single_value_per_row',
    'bar diverging': 'diverging_bar_single_value_per_row',
    'bar-diverging-stacked': 'diverging_stacked_bar_segment_counts',
    'bar diverging stacked': 'diverging_stacked_bar_segment_counts',
    'bar-stacked': 'bar_horizontal_stacked',
    'bar stacked': 'bar_horizontal_stacked',
    'boxplot': 'boxplot',
    'bubble': 'scatter_bubble',
    'bullet': 'bullet_graph',
    'bump': 'line_ranked_bump',
    'candlestick': 'candlestick',
    'chord': 'chord',
    'circle-timeline': 'proportional_circles',
    'circle timeline': 'proportional_circles',
    'cluster': 'cluster_scatter',
    'column-grouped': 'bar_vertical_grouped',
    'column grouped': 'bar_vertical_grouped',
    'column-magnitude': 'bar_vertical',
    'column magnitude': 'bar_vertical',
    'column-ordered': 'bar_vertical_sorted',
    'column ordered': 'bar_vertical_sorted',
    'column-stacked': 'bar_vertical_stacked',
    'column stacked': 'bar_vertical_stacked',
    'dendrogram': 'dendrogram',
    'density': 'density_curve_kde',
    'dot-plot': 'dumbbell',
    'dot plot': 'dumbbell',
    'doughnut': 'donut',
    'funnel': 'funnel',
    'heatmap': 'heatmap',
    'XY-heatmap': 'heatmap',
    'histogram': 'histogram',
    'line': 'line',
    'line-column': 'line_bar_combo',
    'line column': 'line_bar_combo',
    'line and bar combo': 'line_bar_combo',
    'geo_map': 'geo_map',
    'geo-map': 'geo_map',
    'geographic map': 'geo_map',
    'lollipop h': 'lollipop_horizontal',
    'lollipop-v': 'lollipop_vertical',
    'lollipop v': 'lollipop_vertical',
    'pie': 'pie',
    'polar area': 'polar_area',
    'population-pyramis': 'population_pyramid',
    'population pyramis': 'population_pyramid',
    'population pyramids': 'population_pyramid',
    'priestley-timeline': 'gantt',
    'priestley timeline': 'gantt',
    'radar': 'radar',
    'sankey': 'sankey',
    'scatterplot': 'scatter',
    'scatterplot-connected': 'connected_scatter',
    'scatterplot connected': 'connected_scatter',
    'slope': 'slope_two_point',
    'slope-ranking': 'slope_two_point',
    'slope-timeline': 'slope_two_point',
    'spine': 'diverging_100_stacked_bar_likert_percent',
    'stepped line': 'line_stepped',
    'treemap': 'treemap',
    'violin': 'violin',
    'waterfall': 'waterfall',
    'logistic regression': 'logistic_regression_curve',
    'ridgeline': 'ridgeline',
    'hexbin_chart': 'hexbin_chart',
    'forest': 'forest',
    'volcano_plot': 'volcano_plot',
  };
  
  const slug = (s: string) => String(s || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  const items: ChartItem[] = Array.from(byName.values()).map((m) => {
    const icon = resolveIconSrc(m.icon_file);
    // Rename XY heatmap to heatmap
    const normalizedName = /xy[-_ ]?heatmap/i.test(m.name) ? 'heatmap' : m.name;
    const normalizedId = /xy[-_ ]?heatmap/i.test(m.id) ? 'heatmap' : m.id;
    const pretty = renameMap[normKey(normalizedName)] || normalizedName;
    
    // Try to get chartcard ID directly from mapping
    const chartcardId = uiNameToChartcardId[m.id] || uiNameToChartcardId[normalizedName] || uiNameToChartcardId[normKey(m.id)];
    
    // If we have a direct mapping and it exists in chartcard.json, use it
    let candidate: string;
    if (chartcardId && cardIds.has(chartcardId)) {
      candidate = chartcardId;
    } else {
      // Fallback to previous logic: try to match by slugging the pretty name
      const canonicalFromPretty = slug(pretty);
      const canonicalFromId = slug(normalizedId);
      candidate = cardIds.has(canonicalFromPretty)
        ? canonicalFromPretty
        : (cardIds.has(canonicalFromId) ? canonicalFromId : normalizedId);
      // If catalog is unavailable, default to pretty-name slug to best approximate canonical id
      if (cardIds.size === 0) {
        candidate = canonicalFromPretty;
      }
    }
    
    return {
      id: candidate,
      name: pretty,
      icon,
      description: m.description,
      category: m.category,
      bestUse: m.best_use,
    };
  });

  // If we could not load the cards catalog, warn loudly so issues don't silently degrade
  if (!cards || cardIds.size === 0) {
    try { console.warn('[charts] Failed to load chartcard.json; builder IDs may not be canonical'); } catch {}
  }

  chartsCache = items;
  return chartsCache;
}


